/** The target pathway: the two points a commitment states, and the modelled values beside them.
 *
 * ## What is drawn, and only that
 *
 * **Every mark is a stored figure.** The baseline (a measured year and its figure), the level the
 * commitment implies at the horizon (derived server-side from the target, never here), and one
 * marker per version of the assumptions at the horizon year, each *modelled* by the server's own
 * scenario run. Nothing is interpolated: there is no year-by-year path, no "expected trajectory"
 * and no measured line, because none of those exists in this product.
 *
 * **The line between the two stated points is a guide and says so.** A straight line from a
 * baseline to a target reads as a path somebody planned. It is drawn dashed and the caption says it
 * only joins the two stated points; a reader who wants the year-by-year path is told there is none.
 *
 * **Progress is not drawn.** Measured emissions have their own boundary, allocation and receipts,
 * and a chart that put the inventory on this axis would produce a progress figure with no receipt
 * behind it - rule 3's whole subject. The plan screen says the comparison is not built; this chart
 * does not make the omission look like an oversight by leaving a gap where the line would go.
 *
 * **A money price never moves a mark.** `ProjectionOut` is computed by the server from inputs stated
 * as multipliers; a price in money is recorded and named as not used. Nothing here reads one.
 *
 * ## Shapes and words, not colour alone
 *
 * A circle is measured, a diamond is the commitment, a hollow square is modelled. The legend names
 * each in words with its figure, and the hidden table (ADR-0090's first rule) carries the same rows,
 * so the picture is never the only carrier of a number. The brand green is not used: `--accent` is
 * chrome and a target is not good news.
 *
 * Geometry converts the decimal strings with `Number`, which is presentation only (rule 5 puts
 * rounding in the presentation layer); every printed figure is the stored string, trimmed of the
 * scale the column added and changed in no other way.
 */

import { motion, useReducedMotion } from "motion/react";
import { useRef } from "react";

import type { PlanTarget } from "../api/queries";
import { useScenarios } from "../api/queries";
import { ChartTable } from "../components/charts/ChartTable";
import { CHART, MARK_LABEL, TICK_LABEL } from "../components/charts/chartTokens";
import { useChartWidth } from "../components/charts/useChartWidth";
import { statedQuantity } from "../lib/format";
import type { Mark } from "./pathwayMarks";
import { pathwayMarks } from "./pathwayMarks";
import own from "./TransitionPathway.module.css";

const HEIGHT = 220;
const PAD = { top: 14, right: 22, bottom: 30, left: 22 };

function Glyph({ kind }: { kind: Mark["kind"] }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" className={own.glyph}>
      {kind === "baseline" ? (
        <circle cx="7" cy="7" r="5" fill={CHART.ink} />
      ) : kind === "commitment" ? (
        <path d="M7 1.5 12.5 7 7 12.5 1.5 7Z" fill={CHART.inkSoft} />
      ) : (
        <rect x="2.5" y="2.5" width="9" height="9" fill={CHART.surface} stroke={CHART.complete} strokeWidth="2" />
      )}
    </svg>
  );
}

export function TransitionPathway({
  target,
  shown = null,
  onShow,
  modelled = true,
}: {
  target: PlanTarget;
  /** `false` draws the two stated points and nothing modelled, and makes no scenario read. The
   * dashboards pass it: a scenario is never a dashboard tile (ADR-0193, D-1). */
  modelled?: boolean;
  /** The assumptions version drawn as a scenario line, or `null` for none. */
  shown?: number | null;
  /** Present when the screen lets a reader choose the scenario; absent where the chart is only a
   * glance (the Executive dashboard), which then draws no scenario line at all. */
  onShow?: (version: number | null) => void;
}) {
  const reduce = useReducedMotion();
  const scenarios = useScenarios(target.key, modelled);
  const box = useRef<HTMLDivElement>(null);
  const width = useChartWidth(box, 420);

  const marks = pathwayMarks(target, scenarios.error || !modelled ? [] : (scenarios.data?.projections ?? []));
  const start = marks[0];
  const end = marks[1];
  const values = marks.map((mark) => Number(mark.amount));
  const drawable =
    start !== undefined &&
    end !== undefined &&
    target.horizon_year > target.baseline_year &&
    values.every((value) => Number.isFinite(value) && value >= 0) &&
    Math.max(...values) > 0;

  const chosen = shown === null ? undefined : marks.find((mark) => mark.key === `modelled-${shown}`);
  const scenarioMarks = marks.filter((mark) => mark.kind === "modelled");

  const top = drawable ? Math.max(...values) * 1.08 : 1;
  const x = (year: number) =>
    PAD.left +
    ((year - target.baseline_year) / Math.max(1, target.horizon_year - target.baseline_year)) *
      (width - PAD.left - PAD.right);
  const y = (value: number) => HEIGHT - PAD.bottom - (value / top) * (HEIGHT - PAD.top - PAD.bottom);

  return (
    <div data-testid="pathway">
      {onShow && scenarioMarks.length > 0 ? (
        <div className={own.picker} role="group" aria-label="Scenario drawn on the pathway" data-testid="pathway-picker">
          <button
            type="button"
            className={own.pick}
            aria-pressed={shown === null}
            onClick={() => onShow(null)}
          >
            None
          </button>
          {scenarioMarks.map((mark) => {
            const version = Number(mark.key.replace("modelled-", ""));
            return (
              <button
                key={mark.key}
                type="button"
                className={own.pick}
                aria-pressed={shown === version}
                onClick={() => onShow(version)}
                data-testid="pathway-pick"
                data-assumption-version={version}
              >
                Assumptions v{version}
              </button>
            );
          })}
        </div>
      ) : null}
      {!drawable ? (
        <p className={own.cannot} data-testid="pathway-not-drawn">
          There is nothing honest to draw here: the horizon is not after the baseline, or a stated
          figure is not a positive number. The two points are listed below as they are stored.
        </p>
      ) : (
        <div ref={box} className={own.frame}>
          <svg
            width={width}
            height={HEIGHT}
            viewBox={`0 0 ${width} ${HEIGHT}`}
            aria-hidden="true"
            focusable="false"
            className={own.svg}
          >
            {/* The two axes: a floor at zero, and the first year. */}
            <line x1={PAD.left} x2={width - PAD.right} y1={y(0)} y2={y(0)} stroke={CHART.axis} />
            <line x1={PAD.left} x2={PAD.left} y1={PAD.top} y2={y(0)} stroke={CHART.grid} />
            <text x={PAD.left} y={HEIGHT - 8} textAnchor="start" {...TICK_LABEL}>
              {target.baseline_year}
            </text>
            <text x={width - PAD.right} y={HEIGHT - 8} textAnchor="end" {...TICK_LABEL}>
              {target.horizon_year}
            </text>
            <text x={PAD.left + 4} y={y(0) - 5} textAnchor="start" {...TICK_LABEL}>
              0
            </text>

            {/* **Joins the two stated points and nothing else.** Dashed, because a solid line reads
                as a path somebody planned. */}
            <line
              x1={x(start.year)}
              y1={y(Number(start.amount))}
              x2={x(end.year)}
              y2={y(Number(end.amount))}
              stroke={CHART.muted}
              strokeWidth="1.5"
              strokeDasharray="5 4"
              data-testid="pathway-guide"
            />

            {/* **The scenario, and only the one chosen.** Its own dash (shorter than the guide's) and
                its own token, labelled on the mark itself: a scenario is a recipe, never a figure
                (ADR-0193). Moving the choice moves the line; under reduced motion it jumps. */}
            {chosen ? (
              <g data-testid="pathway-scenario" data-assumption-version={chosen.key.replace("modelled-", "")}>
                <motion.line
                  x1={x(start.year)}
                  y1={y(Number(start.amount))}
                  initial={false}
                  animate={{ x2: x(chosen.year), y2: y(Number(chosen.amount)) }}
                  transition={reduce ? { duration: 0 } : { duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  stroke={CHART.complete}
                  strokeWidth="2"
                  strokeDasharray="2 3"
                />
                <motion.text
                  initial={false}
                  animate={{ x: x(chosen.year), y: y(Number(chosen.amount)) - 12 }}
                  transition={reduce ? { duration: 0 } : { duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  textAnchor="end"
                  {...TICK_LABEL}
                >
                  Scenario, not a figure
                </motion.text>
              </g>
            ) : null}

            {marks.map((mark) => {
              const cx = x(mark.year);
              const cy = y(Number(mark.amount));
              return mark.kind === "baseline" ? (
                <circle key={mark.key} cx={cx} cy={cy} r="6" fill={CHART.ink} data-mark={mark.kind} />
              ) : mark.kind === "commitment" ? (
                <path
                  key={mark.key}
                  d={`M${cx} ${cy - 7} L${cx + 7} ${cy} L${cx} ${cy + 7} L${cx - 7} ${cy} Z`}
                  fill={CHART.inkSoft}
                  data-mark={mark.kind}
                />
              ) : (
                <rect
                  key={mark.key}
                  x={cx - (chosen === mark ? 7 : 5)}
                  y={cy - (chosen === mark ? 7 : 5)}
                  width={chosen === mark ? 14 : 10}
                  height={chosen === mark ? 14 : 10}
                  fill={CHART.surface}
                  stroke={CHART.complete}
                  strokeWidth={chosen === mark ? 3 : 2}
                  data-mark={mark.kind}
                  data-shown={chosen === mark ? "true" : undefined}
                />
              );
            })}
            <text x={width / 2} y={HEIGHT - 8} textAnchor="middle" {...MARK_LABEL}>
              {marks[0]?.unit ?? ""}
            </text>
          </svg>
        </div>
      )}

      <ul className={own.legend} data-testid="pathway-legend">
        {marks.map((mark) => (
          <li key={mark.key} className={own.legendRow} data-mark={mark.kind}>
            <Glyph kind={mark.kind} />
            <span className={own.legendLabel}>{mark.label}</span>
            <span className={`${own.legendValue} num`}>
              {statedQuantity(mark.amount)} {mark.unit}
            </span>
            <span className={own.legendBasis}>{mark.basis}</span>
          </li>
        ))}
      </ul>

      {scenarios.error && modelled ? (
        <p className={own.cannot} data-testid="pathway-no-modelled">
          The modelled values could not be read, so only the stated points are shown.
        </p>
      ) : null}

      <p className={own.caption}>
        {modelled
          ? "The dashed line only joins the two stated points; it is not a year-by-year path and not a forecast. Modelled markers are what each version of the assumptions makes the baseline become. Measured emissions are not drawn: comparing them with the target is not available yet."
          : "The dashed line only joins the two stated points. It is not a year-by-year path. Measured emissions are not drawn: comparing them with the target is not available yet."}
      </p>

      <ChartTable
        caption={modelled ? "Target pathway: the stated points and the modelled values" : "Target pathway: the stated points"}
        head={["Point", "Year", "Value", "Basis"]}
        rows={marks.map((mark) => [
          mark.label,
          String(mark.year),
          `${statedQuantity(mark.amount)} ${mark.unit}`,
          mark.basis,
        ])}
      />
    </div>
  );
}
